import { useEffect, useMemo, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { filterBlocks, type BlockDef } from './blocks'
import { BlockAskForm } from './BlockAskForm'
import { BlockChoices } from './BlockChoices'

/**
 * The '/' menu. The interaction people arrive expecting from Notion, and the
 * reason a writer never has to know that tabs need four colons.
 *
 * It watches the document rather than installing an input rule, so the trigger
 * text stays a normal part of the paragraph until something is chosen — and
 * dismissing it leaves exactly what was typed.
 */
/**
 * Where the `/query` being typed is, right now.
 *
 * Derived from the current state every time rather than remembered. The
 * position used to be stored when the menu opened and reused when something was
 * chosen — and any replacement of the document in between (opening a page,
 * reverting an edit) left it pointing past the end, so choosing a block threw
 * "Position N out of range" and inserted nothing. Positions are not values that
 * survive a document changing underneath them.
 */
function triggerAt(editor: Editor): { from: number; to: number; query: string } | null {
  const { state } = editor
  const { from, empty } = state.selection
  if (!empty) return null

  const parent = state.selection.$from.parent
  if (parent.type.name === 'codeBlock') return null

  const before = parent.textBetween(0, state.selection.$from.parentOffset, '\n', '\n')
  const match = /(?:^|\s)\/([\w-]*)$/.exec(before)
  if (!match) return null

  const query = match[1] ?? ''
  const start = from - query.length - 1
  // Belt as well as braces: a slash cannot be at a negative position, and the
  // range has to be inside the document that exists now.
  if (start < 0 || from > state.doc.content.size) return null
  return { from: start, to: from, query }
}

export function SlashMenu({ editor }: { editor: Editor }) {
  const [query, setQuery] = useState<string | null>(null)
  const [at, setAt] = useState<{ left: number; top: number } | null>(null)
  const [index, setIndex] = useState(0)
  /**
   * The block that has been chosen and is asking its one question.
   *
   * The panel becomes the question, in the place it was offered from, at the
   * caret the block is about to be inserted at. It was `window.prompt`, drawn
   * by the operating system over the very spot it was asking about.
   */
  const [asking, setAsking] = useState<BlockDef | null>(null)
  const items = useMemo(() => filterBlocks(query ?? ''), [query])

  /**
   * Read by the document watcher below, which must not close the question.
   *
   * Choosing a block deletes the `/query` text first, and that deletion is an
   * editor update — so the watcher fires, finds no trigger, and would close a
   * panel that has just become a form. A ref rather than a dependency, for the
   * same reason the key handler uses them: one listener, always current.
   */
  const askingRef = useRef<BlockDef | null>(null)
  askingRef.current = asking

  useEffect(() => {
    const update = () => {
      if (askingRef.current) return
      const found = triggerAt(editor)
      if (!found) return close()

      setQuery(found.query)
      setIndex(0)
      const coords = editor.view.coordsAtPos(found.to)
      setAt({ left: coords.left, top: coords.bottom + 6 })
    }
    const close = () => {
      setQuery(null)
      setAt(null)
    }

    editor.on('selectionUpdate', update)
    editor.on('update', update)
    return () => {
      editor.off('selectionUpdate', update)
      editor.off('update', update)
    }
  }, [editor])

  /**
   * What the menu is showing RIGHT NOW, for the key handler to read.
   *
   * The handler used to close over `items` and `index` and be re-registered by
   * an effect keyed on them. Between a keystroke and that effect running, the
   * listener on the window was the previous render's — holding the list for a
   * shorter query. Pressing Enter then ran whatever was first in THAT list,
   * so choosing "Divider" could insert a blockquote: the menu showed one thing
   * and the keypress chose from another.
   *
   * Refs, so there is exactly one listener and it always reads the current
   * state. Registration no longer depends on the values it uses, which is the
   * property that was missing.
   */
  const itemsRef = useRef(items)
  const indexRef = useRef(index)
  itemsRef.current = items
  indexRef.current = index

  useEffect(() => {
    if (query === null) return
    const onKey = (e: KeyboardEvent) => {
      const showing = itemsRef.current
      const at = indexRef.current
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setIndex((i) => (i + 1) % Math.max(showing.length, 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setIndex((i) => (i - 1 + showing.length) % Math.max(showing.length, 1))
      } else if (e.key === 'Enter' && showing[at]) {
        e.preventDefault()
        choose(showing[at]!)
      } else if (e.key === 'Escape') {
        setQuery(null)
        setAt(null)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
    // Only whether the menu is open. The values the handler reads come from
    // refs on purpose — see above.
  }, [query]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!at) return null

  if (asking?.ask) {
    const block = asking
    return (
      <div className="slash" style={{ left: at.left, top: at.top }}>
        <BlockAskForm
          ask={asking.ask}
          className="slash__ask"
          onAnswer={(answer) => {
            block.run(editor, answer)
            setAsking(null)
            setAt(null)
          }}
          onCancel={() => {
            setAsking(null)
            setAt(null)
            editor.chain().focus().run()
          }}
        />
      </div>
    )
  }

  if (query === null || items.length === 0) return null

  return (
    <div className="slash" style={{ left: at.left, top: at.top }} role="listbox">
      <BlockChoices
        items={items}
        active={index}
        optionRole="option"
        onHover={setIndex}
        onChoose={choose}
      />
    </div>
  )

  function choose(block: BlockDef) {
    // Found again from the current document, not remembered from when the menu
    // opened. If the trigger is no longer there, the document moved and there is
    // nothing to replace — inserting anyway is how a block lands in the wrong
    // place, or throws.
    const found = triggerAt(editor)
    setQuery(null)
    if (!found) {
      setAt(null)
      return
    }

    /*
      The ref by hand, BEFORE the deletion below.

      `deleteRange` fires the document watcher synchronously, and `setAsking`
      has not landed by then — so the watcher saw a null ref, found no trigger,
      and closed the panel this is about to reuse. The question opened and
      vanished in the same tick, which from the outside is a menu that does
      nothing.
    */
    if (block.ask) askingRef.current = block

    // Remove the '/query' text first, so the trigger never survives insertion —
    // and before any question is asked, because the text is still a trigger
    // while it is on the page and the watcher would reopen the list over the
    // question.
    editor.chain().focus().deleteRange({ from: found.from, to: found.to }).run()
    if (block.ask) {
      // `at` is kept: the question opens where the '/' was typed.
      setAsking(block)
      return
    }
    setAt(null)
    block.run(editor, '')
  }
}
